/**
 * The console's half of `redeemImpersonationHandoff` (plan-002 RB-18, design §15.7.8, IC-1, IC-2).
 *
 * ── WHY THE GATEWAY MEMBER IS OPTIONAL, FOR NOW ───────────────────────────────────────────
 * Every other operation is a slice under `../../api/slice/` that `api-gateway.ts` spreads and
 * the sandbox implements. RB-18 owns neither of those files in its wave, so the method is added
 * to `ConsoleGateway` here, by declaration merging, as an OPTIONAL member, and the slice that
 * implements it against the API is exported below for the composition to spread. A gateway that
 * does not carry it — the sandbox, which cannot manufacture an operator's grant, or an API
 * gateway the slice has not been spread into yet — makes the hand-off screen say plainly that
 * support sessions cannot be opened here. It never pretends to succeed.
 *
 * The wire shape comes from the generated client (IC-1): the route sends exactly the two values
 * the hand-off URL carried and reads nothing but the status. The session body the redemption
 * answers with is not decoded here; the shell reads the new session through
 * `currentAuthSession()` on its next load, which is also the first proof that the new cookie
 * works.
 */
import { assertOk, type WeTalkApiClient } from "@wetalk/api-client";

/** What the hand-off URL carried: the grant from the path, the token from the fragment. */
export interface ImpersonationHandoffRequestView {
  readonly impersonationGrantId: string;
  readonly handoffToken: string;
}

/**
 * `redeemed` — the cookie is set and the console may open. `refused` — the API's one answer for
 * a link that is unknown, used, expired or mismatched (`401 impersonation_handoff_invalid`).
 */
export type ImpersonationHandoffOutcomeView =
  | { readonly kind: "redeemed" }
  | { readonly kind: "refused" };

declare module "../../api/gateway.js" {
  interface ConsoleGateway {
    /**
     * Redeems a WeTalk support hand-off once. Absent on a gateway that cannot open support
     * sessions (see this file's header); the screen says so rather than guessing.
     */
    readonly redeemImpersonationHandoff?: (
      request: ImpersonationHandoffRequestView,
    ) => Promise<ImpersonationHandoffOutcomeView>;
  }
}

/** The API binding, to be spread into `apiGateway` beside the other slices. */
export function impersonationHandoffApiSlice(client: WeTalkApiClient): {
  readonly redeemImpersonationHandoff: (
    request: ImpersonationHandoffRequestView,
  ) => Promise<ImpersonationHandoffOutcomeView>;
} {
  return {
    async redeemImpersonationHandoff(request) {
      const answered = await client.redeemImpersonationHandoff({
        body: {
          impersonation_grant_id: request.impersonationGrantId,
          handoff_token: request.handoffToken,
        },
      });
      if (answered.status === 401) return { kind: "refused" };
      // Anything else that is not a 2xx is a fault, and it surfaces as one.
      assertOk(answered);
      return { kind: "redeemed" };
    },
  };
}
